import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { ChevronRight, Check } from 'lucide-react';
import SEO, { getServiceSchema } from '../components/SEO';

const RealEstatePhotography = () => {
  const serviceSchema = getServiceSchema({
    name: 'Real Estate Photography',
    description: 'Premium real estate photography services for Arizona properties. Professional HDR photography, virtual twilight, and marketing-ready images.',
    url: 'https://rcstudios.net/services/photography',
    image: 'https://customer-assets.emergentagent.com/job_premium-media-8/artifacts/yhs7mv4g_Hero--photography.jpg',
    areaServed: 'Arizona',
    priceRange: '$199-$749'
  });

  return (
    <div className="flex flex-col min-h-screen">
      <SEO 
        title="Real Estate Photography"
        description="Professional real estate photography in Kingman, Lake Havasu City, and Mohave County. HDR photography, virtual twilight, and marketing-ready images starting at $199."
        path="/services/photography"
        image="https://customer-assets.emergentagent.com/job_premium-media-8/artifacts/yhs7mv4g_Hero--photography.jpg"
        jsonLd={serviceSchema}
      />
      {/* Hero Image - Clean Image Only */}
      <section className="hero-main relative h-[400px] md:h-[480px] lg:h-[520px] overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://customer-assets.emergentagent.com/job_premium-media-8/artifacts/yhs7mv4g_Hero--photography.jpg"
            alt="Luxury home exterior at twilight"
            className="w-full h-full object-cover object-center"
          />
          {/* Subtle cinematic vignette for navbar contrast only */}
          <div className="absolute inset-x-0 top-0 h-28 bg-gradient-to-b from-[#050816]/50 to-transparent"></div>
        </div>
      </section>

      {/* Breadcrumb Section */}
      <section className="bg-[#FAF9F6] border-b border-gray-100">
        <div className="max-w-6xl mx-auto px-6 py-5 md:py-6">
          <div className="flex items-center gap-3">
            <Link 
              to="/services" 
              className="text-[11px] font-medium tracking-[0.2em] uppercase text-[#F5C056] hover:text-[#F5C056]/80 transition-colors"
            >
              Services
            </Link>
            <span className="text-[#050816]/30 text-xs">/</span>
            <span className="text-[11px] font-semibold tracking-[0.2em] uppercase text-[#050816]">
              Real Estate Photography
            </span>
          </div>
          <div className="w-12 h-[1.5px] bg-[#F5C056]/40 mt-4"></div>
        </div>
      </section>

      {/* THE SERVICE SECTION - Moved to top */}
      <section className="bg-[#FAF9F6]">
        <div className="container mx-auto px-6 max-w-[1200px]">
          
          {/* Section Title, Subheadline & Body Copy */}
          <div className="pt-12 pb-10">
            <div className="max-w-[900px] mx-auto text-center">
              <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase mb-4">The Service</p>
              <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-foreground mb-6">
                Real Estate Photography That Sells
              </h2>
              <p className="text-lg md:text-xl text-muted-foreground leading-relaxed max-w-[680px] mx-auto">
                Crisp, professionally lit imagery designed to elevate listings, attract serious buyers, and help your properties stand out in competitive markets.
              </p>
            </div>
          </div>

          {/* Two-Column Editorial Layout */}
          <div className="bg-gradient-to-b from-[#FAF9F6] to-white rounded-2xl mb-12">
            <div className="max-w-[1000px] mx-auto px-8 md:px-12 py-12 md:py-14">
              <div className="grid md:grid-cols-2 gap-12 md:gap-0">
                
                {/* Left Column - Statement Lines */}
                <div className="md:pr-12 md:border-r md:border-[#E8E4DF]">
                  <div className="space-y-10">
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      Clean, modern photography that highlights architectural detail, natural light, and flow.
                    </p>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      Thoughtfully composed imagery — from curb appeal to first impression.
                    </p>
                    <p className="text-lg text-muted-foreground leading-relaxed">
                      Designed for real estate professionals who value consistency, speed, and quality.
                    </p>
                  </div>
                  
                  {/* Emphasis Line */}
                  <p className="mt-12 text-base text-foreground/70 italic tracking-wide">
                    Crafted to represent your listings at their best.
                  </p>
                </div>
                
                {/* Right Column - Feature Rail */}
                <div className="md:pl-12">
                  <p className="text-xs font-semibold tracking-widest text-[#F5C056] uppercase mb-8">What You Get</p>
                  <div className="space-y-6">
                    {[
                      "MLS-ready delivery",
                      "Fast turnaround",
                      "Consistent visual style",
                      "Reliable scheduling"
                    ].map((item, index) => (
                      <div key={index} className="flex items-center gap-4">
                        <div className="w-8 h-px bg-[#F5C056]"></div>
                        <span className="text-foreground font-medium">{item}</span>
                      </div>
                    ))}
                  </div>
                </div>
                
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════
          PART A & B — CINEMATIC EDITORIAL FEATURE (Dark Background)
          ═══════════════════════════════════════════════════════════════════════════ */}
      <section className="bg-[#050816] py-12 md:py-14">
        <div className="max-w-6xl mx-auto px-6">
          
          {/* PART A — Editorial Intro */}
          <div className="mb-10">
            <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase mb-4">
              Portfolio Highlights
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-white mb-4 max-w-3xl leading-tight">
              A premium finish—across every property style.
            </h2>
            <p className="text-lg text-white/60 leading-relaxed max-w-2xl">
              Studio-grade composition, true-to-life color, and a luxury editorial look—built for MLS and beyond.
            </p>
            {/* Gold hairline divider */}
            <div className="w-20 h-px bg-[#F5C056]/40 mt-6"></div>
          </div>

          {/* PART B — Hero Visual + Studio Signature Cards */}
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
            
            {/* LEFT — Hero Visual (Collage Image) */}
            <div className="order-1">
              <div className="relative rounded-2xl overflow-hidden border border-white/10 shadow-2xl bg-[#0a0f1a]">
                {/* Feature Board Tag */}
                <div className="absolute top-4 left-4 z-10">
                  <span className="px-3 py-1.5 bg-[#F5C056] text-[#050816] text-[9px] font-bold uppercase tracking-widest rounded">
                    Feature Board
                  </span>
                </div>
                <img 
                  src="https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/ms5n9zd8_ChatGPT%20Image%20Feb%2010%2C%202026%2C%2006_37_02%20PM.png"
                  alt="Real estate marketing showcase - luxury homes and interiors"
                  className="w-full h-auto max-h-[600px] object-contain"
                />
              </div>
              {/* Caption */}
              <p className="text-white/40 text-xs mt-4 text-center">
                Example marketing board — interior + exterior variety with consistent grading.
              </p>
            </div>

            {/* RIGHT — Studio Signature Cards */}
            <div className="order-2">
              <p className="text-[10px] font-semibold tracking-[0.2em] text-[#F5C056] uppercase mb-6">
                Studio Signatures
              </p>
              
              {/* Signature Cards Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                {[
                  { 
                    num: "01", 
                    title: "Editorial Color Discipline", 
                    desc: "Cohesive tone across the full set" 
                  },
                  { 
                    num: "02", 
                    title: "Clean Vertical Lines", 
                    desc: "Architecture stays true and premium" 
                  },
                  { 
                    num: "03", 
                    title: "Detail-First Interiors", 
                    desc: "Natural balance, crisp finishes" 
                  },
                  { 
                    num: "04", 
                    title: "Curb Appeal Craft", 
                    desc: "Landscaping clarity, premium contrast" 
                  },
                  { 
                    num: "05", 
                    title: "Marketing-Ready Delivery", 
                    desc: "MLS + social crops + print exports" 
                  },
                  { 
                    num: "06", 
                    title: "Reliable Turnaround", 
                    desc: "Streamlined workflow for agents" 
                  },
                ].map((sig, index) => (
                  <div 
                    key={index} 
                    className="bg-white/5 border border-white/10 rounded-2xl p-5 hover:bg-white/[0.08] hover:border-white/20 transition-all duration-300 hover:shadow-lg hover:shadow-[#F5C056]/5"
                  >
                    <p className="text-[9px] font-bold tracking-widest text-[#F5C056] uppercase mb-2">
                      Signature {sig.num}
                    </p>
                    <p className="text-white font-semibold text-sm mb-1">
                      {sig.title}
                    </p>
                    <p className="text-white/50 text-xs leading-relaxed">
                      {sig.desc}
                    </p>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ═══════════════════════════════════════════════════════════════════════════
          PART C — CURATED GALLERY STRIP (Cream Background)
          ═══════════════════════════════════════════════════════════════════════════ */}
      <section className="bg-[#FAF9F6] py-12 md:py-14">
        <div className="max-w-6xl mx-auto px-6">
          
          {/* Gallery Header */}
          <div className="mb-8">
            <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase mb-3">
              Property Examples
            </p>
            <h3 className="font-serif text-2xl md:text-3xl lg:text-4xl text-[#050816] mb-2">
              Interiors, exteriors, and everything in between.
            </h3>
            <p className="text-gray-500 text-base max-w-xl">
              A few samples that show range—final delivery is polished for consistency.
            </p>
          </div>

          {/* Horizontal Scroll Gallery - Desktop & Mobile */}
          <div className="overflow-x-auto pb-4 -mx-6 px-6 scrollbar-hide">
            <div className="flex gap-5" style={{ width: 'max-content' }}>
              {[
                { 
                  src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/c6y8oy0g_home-design.jpg",
                  alt: "Luxury bedroom interior",
                  tag: "Bedroom"
                },
                { 
                  src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/xxf2bzmc_IMG_5655.jpg",
                  alt: "Modern kitchen interior",
                  tag: "Kitchen"
                },
                { 
                  src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/p17vnkte_a19718b9035f6a0273f3b94a3e2f1d75l-b3114780124od-w480_h480_q80.jpg",
                  alt: "Small home exterior",
                  tag: "Exterior"
                },
                { 
                  src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/uxj0irl0_istockphoto-2155879454-612x612.jpg",
                  alt: "Modern home at twilight",
                  tag: "Twilight"
                },
                { 
                  src: "https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/q6fegbed_626cb472d229f9f08bb303c4f69e932c-full.jpg",
                  alt: "Modern coastal home exterior",
                  tag: "Coastal"
                }
              ].map((image, index) => (
                <div 
                  key={index} 
                  className="relative flex-shrink-0 w-[320px] md:w-[360px] overflow-hidden rounded-2xl shadow-md hover:shadow-xl border border-black/5 bg-white transition-all duration-300 group"
                >
                  <img 
                    src={image.src}
                    alt={image.alt}
                    className="w-full h-56 md:h-64 object-cover transition-transform duration-500 group-hover:scale-[1.02]"
                  />
                  {/* Tag Chip */}
                  <div className="absolute top-4 left-4">
                    <span className="px-3 py-1.5 bg-[#050816] text-[#F5C056] text-[9px] font-bold uppercase tracking-widest rounded-full">
                      {image.tag}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* CTA Section */}
          <div className="mt-10 text-center">
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium transition-all shadow-lg hover:shadow-xl"
              >
                <Link to="/book">
                  Order Real Estate Photography
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button 
                asChild 
                className="bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium transition-all"
              >
                <Link to="/pricing">
                  View Pricing
                </Link>
              </Button>
            </div>
            {/* Reassurance Line */}
            <p className="text-gray-400 text-xs mt-4 italic">
              Designed for agents who want a premium first impression.
            </p>
          </div>

        </div>
      </section>

      {/* Trust Callout Section */}
      <section className="bg-[#FAF9F6] pb-12">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="max-w-[900px] mx-auto">
            <div className="bg-[#050816] rounded-2xl p-8 md:p-10 text-center">
              <div className="flex justify-center mb-5">
                <div className="w-12 h-12 rounded-full bg-[#F5C056]/20 flex items-center justify-center">
                  <Check className="w-6 h-6 text-[#F5C056]" />
                </div>
              </div>
              <h3 className="font-serif text-2xl md:text-3xl text-white mb-4">
                Designed for Real Estate Professionals
              </h3>
              <p className="text-gray-300 text-lg leading-relaxed max-w-[620px] mx-auto">
                I understand MLS requirements, deadlines, and the importance of delivering images that support your brand and help listings move faster.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* VIRTUAL TWILIGHT UPGRADE SECTION */}
      <section className="py-12 md:py-14 bg-[#FAF9F6]">
        <div className="max-w-[1200px] mx-auto px-6">
          
          {/* Section Header */}
          <div className="mb-10">
            <p className="text-sm font-medium tracking-widest text-[#F5C056] uppercase mb-3">
              Virtual Twilight Upgrade
            </p>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl text-[#050816] mb-4">
              Daylight In. Twilight Out.
            </h2>
            <p className="text-lg text-gray-600 leading-relaxed max-w-[700px]">
              We transform your daytime exterior into a cinematic twilight hero image — with realistic lighting, believable sky, and a premium finish.
            </p>
          </div>

          {/* Two-Column Featured Panel */}
          <div className="bg-white rounded-2xl shadow-lg border border-black/5 overflow-hidden mb-8">
            <div className="grid lg:grid-cols-2">
              
              {/* Left Column - Copy + Capability List */}
              <div className="p-8 md:p-12 lg:p-14">
                <p className="text-gray-600 leading-relaxed mb-8">
                  Skip the sunset scheduling. Deliver luxury curb appeal on every listing. Our virtual twilight service creates MLS-ready hero images that make properties unforgettable.
                </p>

                {/* Capability Rows */}
                <div className="space-y-5">
                  {[
                    { label: "Realistic Window Glow", desc: "Warm interior lighting added with natural falloff" },
                    { label: "Sky Replacement", desc: "Cinematic dusk sky matched to the scene" },
                    { label: "Color & Contrast Grade", desc: "Premium twilight toning without looking fake" },
                    { label: "Exterior Light Enhancements", desc: "Porch/patio lights subtly enhanced when appropriate" },
                    { label: "Pool & Water Refinement", desc: "Richer water tone and reflections for a luxury feel" },
                    { label: "Landscape Balance", desc: "Preserve greens/desert tones with clean separation" },
                    { label: "MLS-Friendly Output", desc: "Crisp, natural, not over-processed" },
                  ].map((item, index) => (
                    <div key={index} className="flex items-start gap-4">
                      <div className="flex-shrink-0 w-1.5 h-1.5 rounded-full bg-[#F5C056] mt-2.5"></div>
                      <div>
                        <span className="font-semibold text-[#050816] text-sm">{item.label}</span>
                        <span className="text-gray-500 text-sm"> — {item.desc}</span>
                      </div>
                    </div>
                  ))}
                </div>
              </div>

              {/* Right Column - Image Card */}
              <div className="relative bg-[#050816] p-6 md:p-8 flex items-center justify-center">
                {/* Micro Label */}
                <div className="absolute top-4 left-4 md:top-6 md:left-6 z-10">
                  <span className="px-3 py-1.5 bg-[#F5C056]/20 backdrop-blur-sm text-[#F5C056] text-[10px] font-semibold uppercase tracking-widest rounded">
                    Example Transformation
                  </span>
                </div>
                
                {/* Image Container */}
                <div className="relative w-full overflow-hidden rounded-2xl shadow-2xl">
                  <img 
                    src="https://customer-assets.emergentagent.com/job_a600f8cf-0a4c-4fb9-a62c-d0a867142ab8/artifacts/e6n64a95_ChatGPT%20Image%20Feb%2010%2C%202026%2C%2006_03_23%20PM.png"
                    alt="Day to Virtual Twilight transformation"
                    className="w-full h-auto object-cover"
                  />
                  {/* Caption Strip */}
                  <div className="absolute bottom-0 inset-x-0 bg-gradient-to-t from-[#050816]/90 to-transparent py-4 px-5">
                    <p className="text-white/90 text-xs tracking-wide text-center">
                      <span className="text-white/60">Before:</span> Daylight <span className="text-white/40 mx-2">|</span> <span className="text-white/60">After:</span> Virtual Twilight
                    </p>
                  </div>
                </div>
              </div>

            </div>
          </div>

          {/* "Why It Sells" Mini Strip */}
          <div className="bg-[#050816] rounded-2xl p-6 md:p-8 mb-8">
            <div className="grid md:grid-cols-3 gap-8 md:gap-6 text-center md:text-left">
              <div>
                <p className="text-[10px] font-semibold tracking-widest text-[#F5C056] uppercase mb-2">Impact</p>
                <p className="text-white font-medium">Higher click appeal</p>
              </div>
              <div className="md:border-l md:border-white/10 md:pl-6">
                <p className="text-[10px] font-semibold tracking-widest text-[#F5C056] uppercase mb-2">First Impression</p>
                <p className="text-white font-medium">Premium curb appeal</p>
              </div>
              <div className="md:border-l md:border-white/10 md:pl-6">
                <p className="text-[10px] font-semibold tracking-widest text-[#F5C056] uppercase mb-2">Versatility</p>
                <p className="text-white font-medium">Perfect hero for MLS & social</p>
              </div>
            </div>
          </div>

          {/* CTA Row */}
          <div>
            <div className="flex flex-col sm:flex-row gap-4">
              <Button 
                asChild 
                className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium transition-all shadow-lg hover:shadow-xl"
              >
                <Link to="/book?type=other">
                  Order Virtual Twilight
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button 
                asChild 
                className="bg-[#050816] text-white hover:bg-[#050816]/90 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium transition-all"
              >
                <Link to="/contact">
                  Request a Custom Package
                </Link>
              </Button>
            </div>
            <p className="text-gray-500 text-sm mt-4 italic">
              Fast turnaround. Natural look. Built for luxury listings.
            </p>
          </div>

        </div>
      </section>

      {/* Vacant Land */}
      <section className="py-10 bg-[#050816] border-t border-white/5" data-testid="vacant-land-callout">
        <div className="container mx-auto px-6 max-w-[1200px] flex flex-col md:flex-row md:items-center md:justify-between gap-4">
          <div>
            <p className="text-[10px] font-semibold tracking-widest text-[#F5C056] uppercase mb-2">New · Vacant Land</p>
            <h3 className="font-serif text-2xl text-white">Selling land? Drone photos from $75.</h3>
            <p className="text-gray-400 text-sm mt-1">Lot-line overlays on 1 acre and up. Delivered within 24 hours.</p>
          </div>
          <Button asChild className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-12 px-8 text-sm uppercase tracking-widest font-medium" data-testid="vacant-land-callout-btn">
            <Link to="/book?type=land">Book Land Photos</Link>
          </Button>
        </div>
      </section>

      {/* Final CTA Section */}
      <section className="py-12 md:py-14 bg-[#FAF9F6]">
        <div className="container mx-auto px-6 max-w-[1200px]">
          <div className="text-center">
            <h3 className="font-serif text-2xl md:text-3xl text-[#050816] mb-3">
              Ready to elevate your listings?
            </h3>
            <p className="text-gray-600 mb-6 max-w-[500px] mx-auto">
              Let's create imagery that helps your properties stand out and sell faster.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                asChild 
                className="bg-[#F5C056] text-[#050816] hover:bg-[#F5C056]/90 rounded-lg h-12 px-10 text-sm uppercase tracking-widest font-medium transition-all shadow-lg hover:shadow-xl"
              >
                <Link to="/book">
                  Book Real Estate Photography
                  <ChevronRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button 
                asChild 
                variant="outline"
                className="border-gray-300 text-gray-700 hover:bg-gray-50 rounded-lg h-12 px-10 text-sm uppercase tracking-widest font-medium"
              >
                <Link to="/pricing">
                  View Pricing
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default RealEstatePhotography;
